package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

/** Assist-specific composition styles, kept apart from the utility shell's geometry. */
val EditorAssistContentStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .minHeight(0.px)
            .gap(Space.LG)
            .styleModifier {
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule(" > [data-editor-assist-section]") {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
            }
    }
    cssRule(" [data-editor-assist-glossary-list]") {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Card)
            .styleModifier {
                property("box-sizing", "border-box")
                property("list-style", "none")
                property("margin", "0")
                property("padding", "0")
            }
    }
    cssRule(" [data-editor-glossary-term]") {
        Modifier
            .minWidth(0.px)
            .padding(Space.MD)
            .gap(Space.XS)
            .backgroundColor(colorMode.toSitePalette().surface)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .styleModifier {
                property("box-sizing", "border-box")
                property("display", "flex")
                property("flex-direction", "column")
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule(" [data-editor-glossary-term]:last-child") {
        Modifier
            .borderBottom(0.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(bottomLeft = Radius.Card, bottomRight = Radius.Card)
    }
    cssRule(" [data-editor-glossary-term]:first-child") {
        Modifier.borderRadius(topLeft = Radius.Card, topRight = Radius.Card)
    }
    cssRule(" [data-editor-glossary-term]:only-child") {
        Modifier.borderRadius(Radius.Card)
    }
    cssRule(" [data-editor-glossary-header]") {
        Modifier
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("flex-wrap", "wrap")
            }
    }
    cssRule(" [data-editor-glossary-header] > span") {
        Modifier.styleModifier {
            property("max-width", "100%")
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
        }
    }
    cssRule(" [data-editor-glossary-target], [data-editor-glossary-description]") {
        Modifier.styleModifier {
            property("min-width", "0")
            property("overflow-wrap", "anywhere")
            property("white-space", "pre-wrap")
        }
    }
}

val EditorProviderControlsStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "stretch")
            }
    }
    cssRule(" [data-editor-provider-field]") {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .styleModifier {
                property("flex", "1 1 auto")
            }
    }
    cssRule(" [data-editor-provider-actions]") {
        Modifier
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                // `flex-start`, not `space-between`: the chord belongs to the button, and
                // `space-between` flung it to the far edge of a 400px pane where it read as an
                // unattached ornament rather than as this control's shortcut. The source row puts
                // the same two things next to each other and this now matches it.
                property("justify-content", "flex-start")
                property("flex-wrap", "wrap")
            }
    }
    cssRule(" [data-editor-provider-actions] button") {
        Modifier.styleModifier {
            property("max-width", "100%")
            property("white-space", "normal")
        }
    }
    cssRule(" [data-editor-provider-copy]") {
        Modifier
            .fontSize(TypeScale.Micro)
            .lineHeight(1.45)
            .color(colorMode.toSitePalette().textSecondary)
            .styleModifier {
                property("overflow-wrap", "anywhere")
            }
    }
    // A failed generation was written into `data-editor-provider-outcome` and then drawn in the
    // same muted micro type as the neutral "Uses Lingvo's provider connection" blurb beside it —
    // the attribute was emitted and styled nowhere. The translation-memory failure eight rules
    // down already gets `danger`, on the same panel, for the same class of event; a provider
    // failure that reads as a hint is the panel holding two standards for one thing.
    cssRule(" [data-editor-provider-outcome=\"failed\"]") {
        Modifier.color(colorMode.toSitePalette().danger)
    }
    // "Nothing came back" is not a failure and not a result. Warning, so it is legible as neither.
    cssRule(" [data-editor-provider-outcome=\"no-result\"]") {
        Modifier.color(colorMode.toSitePalette().warning)
    }
}

val EditorRankedStreamStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule(" [data-editor-assist-results]") {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Card)
            .styleModifier {
                property("box-sizing", "border-box")
                property("list-style", "none")
                property("margin", "0")
                property("padding", "0")
                property("overflow", "visible")
            }
    }
    cssRule(" [data-editor-assist-results] > li") {
        Modifier
            .minWidth(0.px)
            .padding(Space.MD)
            .backgroundColor(colorMode.toSitePalette().surface)
            .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .styleModifier {
                property("box-sizing", "border-box")
            }
    }
    cssRule(" [data-editor-assist-results] > li:last-child") {
        Modifier
            .borderBottom(0.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(bottomLeft = Radius.Card, bottomRight = Radius.Card)
    }
    cssRule(" [data-editor-assist-results] > li:first-child") {
        Modifier.borderRadius(topLeft = Radius.Card, topRight = Radius.Card)
    }
    cssRule(" [data-editor-assist-results] > li:only-child") {
        Modifier.borderRadius(Radius.Card)
    }
    cssRule(" [data-editor-assist-stream-content]") {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
            }
    }
    cssRule(" [data-editor-assist-result]") {
        Modifier
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("overflow", "visible")
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule(" [data-editor-assist-result-header]") {
        Modifier
            .minWidth(0.px)
            .gap(Space.SM)
            .color(colorMode.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .lineHeight(1.4)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("flex-wrap", "wrap")
            }
    }
    cssRule(" [data-editor-assist-result-header] > span") {
        Modifier.styleModifier {
            property("max-width", "100%")
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
        }
    }
    cssRule(" [data-editor-assist-ordinal-label]") {
        Modifier
            .minWidth(24.px)
            .minHeight(24.px)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textSecondary)
            .borderRadius(Radius.Control)
            .fontSize(TypeScale.Micro)
            .fontWeight(700)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex", "0 0 24px")
                property("font-variant-numeric", "tabular-nums")
            }
    }
    cssRule(" [data-editor-assist-provenance]") {
        Modifier
            .minWidth(0.px)
            .fontWeight(700)
            .color(colorMode.toSitePalette().textPrimary)
            .styleModifier {
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule(" [data-editor-assist-evidence]") {
        Modifier
            .minWidth(0.px)
            .fontSize(TypeScale.Small)
            .lineHeight(1.45)
            .color(colorMode.toSitePalette().textSecondary)
            .styleModifier {
                property("overflow-wrap", "anywhere")
                property("white-space", "pre-wrap")
            }
    }
    cssRule(
        " [data-editor-assist-evidence=\"warning\"]," +
            " [data-editor-assist-evidence=\"stale\"]",
    ) {
        Modifier.color(colorMode.toSitePalette().danger)
    }
    cssRule(" [data-editor-assist-actions]") {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "flex-end")
                property("flex-wrap", "wrap")
                property("overflow", "visible")
            }
    }
    cssRule(" [data-editor-assist-actions] button") {
        Modifier.styleModifier {
            property("max-width", "100%")
            property("white-space", "normal")
        }
    }
    cssRule(" [data-editor-assist-tm-failure]") {
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            .padding(Space.MD)
            .gap(Space.SM)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Control)
            .color(colorMode.toSitePalette().textSecondary)
            .fontSize(TypeScale.Small)
            .lineHeight(1.45)
            .styleModifier {
                property("box-sizing", "border-box")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("flex-wrap", "wrap")
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule(" [data-editor-assist-tm-failure] [data-editor-assist-error-copy]") {
        Modifier
            .minWidth(0.px)
            .color(colorMode.toSitePalette().danger)
            .styleModifier {
                property("flex", "1 1 180px")
                property("overflow-wrap", "anywhere")
            }
    }
    cssRule(" [data-editor-assist-tm-failure] button") {
        Modifier.styleModifier {
            property("max-width", "100%")
            property("white-space", "normal")
        }
    }
    cssRule(" [data-editor-assist-skeleton]") {
        Modifier
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
            }
    }
}

val EditorAssistSuggestionValueStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .fontSize(TypeScale.BodySmall)
        .fontWeight(500)
        .lineHeight(1.55)
        .styleModifier {
            property("overflow-wrap", "anywhere")
            property("white-space", "pre-wrap")
        }
}

val EditorAssistSectionLabelStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
        .color(colorMode.toSitePalette().textPrimary)
        .styleModifier {
            property("letter-spacing", "0")
            property("margin", "0")
            property("overflow-wrap", "anywhere")
        }
}

val EditorAssistMetaStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.Micro)
        .lineHeight(1.45)
        .color(colorMode.toSitePalette().textSecondary)
        .styleModifier {
            property("overflow-wrap", "anywhere")
        }
}
